import React from "react"
import PropTypes from "prop-types"
import Table from "react-bootstrap/Table"
import "bootstrap/dist/css/bootstrap.min.css"
import "./schools.css"
import _ from "lodash"
import { FormattedMessage, useIntl } from "react-intl"
import ApplicationDeadline from "./ApplicationDeadline"
import PieChart from "./PieChart"
import "./schools-admissions.css"

const SectionAdmissions = ({ schoolData = {} }) => {
  const { formatMessage } = useIntl()

  const {
    appliedCount = null,
    acceptedCount = null,
    enrolledCount = null,
    averageSatReading = null,
    averageSatReading25th = null,
    averageSatReading75th = null,
    averageSatMath = null,
    averageSatMath25th = null,
    averageSatMath75th = null,
    actCompositeScore = null,
    actCompositeScore25th = null,
    actCompositeScore75th = null,
    applicationFee = null,
    averageHighSchoolGpa = null,
    requirements = null,
    applicationRequirements = [],
  } = schoolData || {}

  const applicationDeadlineFreshman = new ApplicationDeadline(schoolData?.applicationDeadlineFreshman)
  const applicationDeadlineEarly = new ApplicationDeadline(schoolData?.applicationDeadlineEarly)
  const applicationDeadlineTransfer = new ApplicationDeadline(schoolData?.applicationDeadlineTransfer)

  const ordinal = require("ordinal-js")
  ordinal() //only needed once

  const emptyValue = <span>&mdash;</span>
  const acceptanceRate = schoolData?.acceptanceRate ? _.clamp(Math.round(schoolData?.acceptanceRate), 0, 100) : 100

  const hasTestScores = [
    averageSatReading,
    averageSatReading25th,
    averageSatReading75th,
    averageSatMath,
    averageSatMath25th,
    averageSatMath75th,
    actCompositeScore,
    actCompositeScore25th,
    actCompositeScore75th,
  ].some((score) => score)

  // Check which test score columns have data
  const hasAvgScores = averageSatReading !== null || averageSatMath !== null || actCompositeScore !== null
  const has25thPercentile = averageSatReading25th !== null || averageSatMath25th !== null || actCompositeScore25th !== null
  const has75thPercentile = averageSatReading75th !== null || averageSatMath75th !== null || actCompositeScore75th !== null

  // Check which deadline columns have data
  const hasClosingDates = [applicationDeadlineFreshman?.closingDate, applicationDeadlineEarly?.closingDate, applicationDeadlineTransfer?.closingDate].some((date) => date)
  const hasNotificationDates = [applicationDeadlineFreshman?.notificationDate, applicationDeadlineEarly?.notificationDate, applicationDeadlineTransfer?.notificationDate].some((date) => date)
  const hasFinancialAidDates = [applicationDeadlineFreshman?.financialAidDate, applicationDeadlineEarly?.financialAidDate, applicationDeadlineTransfer?.financialAidDate].some((date) => date)
  const hasRollingAdmissions = [applicationDeadlineFreshman?.isRollingAdmission, applicationDeadlineEarly?.isRollingAdmission, applicationDeadlineTransfer?.isRollingAdmission].some((val) => val !== undefined && val !== null)

  // Only show deadlines table if there's actual data beyond just the Type column
  const hasDeadlines = hasClosingDates || hasNotificationDates || hasFinancialAidDates || hasRollingAdmissions

  const hasApplicationData = 
    (applicationFee && applicationFee?.value !== null) ||
    averageHighSchoolGpa !== null ||
    (applicationRequirements !== null && Array.isArray(applicationRequirements) && applicationRequirements?.length > 0)

  return (
    <>
    <div className="admissions-section-container">
      {!!acceptanceRate && (
        <div className="acceptance-rate">
          <h3 className="tabs-section-heading">
            <FormattedMessage id="acceptance-rate" defaultMessage="Acceptance Rate" />
          </h3>
          <div className="acceptance-rate-container">
            <PieChart
              fractionFilled={acceptanceRate}
              fillColor="#C837D3"
              emptyColor="#D1BCFD"
            />
            <div className="acceptance-rate-stats">
              <ul>
                <li className="percentage">
                  {`${acceptanceRate}%`}
                </li>
                {appliedCount !== null && (
                  <li>
                    {appliedCount?.toLocaleString("en")} <FormattedMessage id="applied" defaultMessage="Applied" />
                  </li>
                )}
                {acceptedCount !== null && (
                  <li>
                    {acceptedCount?.toLocaleString("en")} <FormattedMessage id="accepted" defaultMessage="Accepted" />
                  </li>
                )}
                {enrolledCount !== null && (
                  <li>
                    {enrolledCount?.toLocaleString("en")} <FormattedMessage id="enrolled" defaultMessage="Enrolled" />
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {hasTestScores && (
        <div className="acceptance-test-scores">
          <h3 className="tabs-section-heading">
            <FormattedMessage id="test-scores-accepted" defaultMessage="Test Scores Accepted" />
          </h3>
          <div className="table-responsive">
          <Table responsive className="admissions-table">
            <thead>
              <tr>
                <th>
                  <FormattedMessage id="test" defaultMessage="Test" />
                </th>
                {hasAvgScores && (
                  <th>
                    <FormattedMessage id="avg-score" defaultMessage="Avg. Score" />
                  </th>
                )}
                {has25thPercentile && (
                  <th>
                    <FormattedMessage id="two-five-th-info" defaultMessage="25th % info" />
                  </th>
                )}
                {has75thPercentile && (
                  <th>
                    <FormattedMessage id="seven-five-th-info" defaultMessage="75th % info" />
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <FormattedMessage id="SAT-critical-reading" defaultMessage="SAT Critical Reading" />
                </td>
                {hasAvgScores && <td>{averageSatReading !== null ? averageSatReading : emptyValue}</td>}
                {has25thPercentile && <td>{averageSatReading25th !== null ? averageSatReading25th : emptyValue}</td>}
                {has75thPercentile && <td>{averageSatReading75th !== null ? averageSatReading75th : emptyValue}</td>}
              </tr>
              <tr>
                <td>
                  <FormattedMessage id="SAT-Math" defaultMessage="SAT Math" />
                </td>
                {hasAvgScores && <td>{averageSatMath !== null ? averageSatMath : emptyValue}</td>}
                {has25thPercentile && <td>{averageSatMath25th !== null ? averageSatMath25th : emptyValue}</td>}
                {has75thPercentile && <td>{averageSatMath75th !== null ? averageSatMath75th : emptyValue}</td>}
              </tr>
              <tr>
                <td>
                  <FormattedMessage id="ACT-composite" defaultMessage="ACT Composite" />
                </td>
                {hasAvgScores && <td>{actCompositeScore !== null ? actCompositeScore : emptyValue}</td>}
                {has25thPercentile && <td>{actCompositeScore25th !== null ? actCompositeScore25th : emptyValue}</td>}
                {has75thPercentile && <td>{actCompositeScore75th !== null ? actCompositeScore75th : emptyValue}</td>}
              </tr>
            </tbody>
          </Table>
          </div>
        </div>
      )}
      {hasApplicationData && (
        <div className="application">
          <h3 className="tabs-section-heading">
            <FormattedMessage id="applying" defaultMessage="Applying" />
          </h3>

          {applicationFee && applicationFee?.value !== null && (
            <div className="application-fee">
              <span className="number-stats">
                {applicationFee?.symbol}
                {applicationFee?.value?.toLocaleString("en")}
              </span>
              <FormattedMessage id="application-fee" defaultMessage="application fee" />
            </div>
          )}

          {averageHighSchoolGpa !== null && (
            <div className="application-gpa">
              <span className="number-stats">{averageHighSchoolGpa}</span>
              <FormattedMessage id="avg-hs-GPA-freshmen" defaultMessage="Avg. high school GPA for freshmen" />
            </div>
          )}

          {applicationRequirements !== null && Array.isArray(applicationRequirements) && applicationRequirements?.length > 0 && (
            <div className="application-requirements">
              <strong>
                <FormattedMessage id="requirements" defaultMessage="Requirements" />:
              </strong>
              <ul>
                {applicationRequirements.map((item, idx) => (
                  <li key={idx}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {hasDeadlines && (
        <div className="application-deadlines">
          <h3 className="tabs-section-heading">
            <FormattedMessage id="application-deadlines" defaultMessage="Application Deadlines" />
          </h3>
          <div className="table-responsive">
          <Table responsive className="admissions-table">
            <thead>
              <tr>
                <th>
                  <FormattedMessage id="type" defaultMessage="Type" />
                </th>
                {hasClosingDates && (
                  <th>
                    <FormattedMessage id="application-closing" defaultMessage="Application Closing" />
                  </th>
                )}
                {hasNotificationDates && (
                  <th>
                    <FormattedMessage id="notification-date" defaultMessage="Notification Date" />
                  </th>
                )}
                {hasFinancialAidDates && (
                  <th>
                    <FormattedMessage id="financial-aid" defaultMessage="Financial Aid" />
                  </th>
                )}
                {hasRollingAdmissions && (
                  <th>
                    <FormattedMessage id="rolling-admissions" defaultMessage="Rolling Admissions" />
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <FormattedMessage id="fall-freshmen" defaultMessage="Fall freshmen" />
                </td>
                {hasClosingDates && (
                  <td>
                    {applicationDeadlineFreshman !== null && applicationDeadlineFreshman?.closingDate
                      ? applicationDeadlineFreshman?.closingDate?.monthName + " " + applicationDeadlineFreshman?.closingDate?.dayInt?.toOrdinal()
                      : emptyValue}
                  </td>
                )}
                {hasNotificationDates && (
                  <td>
                    {applicationDeadlineFreshman !== null && applicationDeadlineFreshman?.notificationDate
                      ? applicationDeadlineFreshman?.notificationDate?.monthName + " " + applicationDeadlineFreshman?.notificationDate?.dayInt?.toOrdinal()
                      : emptyValue}
                  </td>
                )}
                {hasFinancialAidDates && (
                  <td>
                    {applicationDeadlineFreshman !== null && applicationDeadlineFreshman?.financialAidDate
                      ? applicationDeadlineFreshman?.financialAidDate?.monthName + " " + applicationDeadlineFreshman?.financialAidDate?.dayInt?.toOrdinal()
                      : emptyValue}
                  </td>
                )}
                {hasRollingAdmissions && (
                  <td>
                    {applicationDeadlineFreshman !== null && applicationDeadlineFreshman?.isRollingAdmission
                      ? formatMessage({ id: "yes", defaultMessage: "Yes" })
                      : formatMessage({ id: "no", defaultMessage: "No" })}
                  </td>
                )}
              </tr>
              <tr>
                <td>
                  <FormattedMessage id="early-decision-plan" defaultMessage="Early decision plan" />
                </td>
                {hasClosingDates && (
                  <td>
                    {applicationDeadlineEarly !== null && applicationDeadlineEarly?.closingDate
                      ? applicationDeadlineEarly?.closingDate?.monthName + " " + applicationDeadlineEarly?.closingDate?.dayInt?.toOrdinal()
                      : emptyValue}
                  </td>
                )}
                {hasNotificationDates && (
                  <td>
                    {applicationDeadlineEarly !== null && applicationDeadlineEarly?.notificationDate
                      ? applicationDeadlineEarly?.notificationDate?.monthName + " " + applicationDeadlineEarly?.notificationDate?.dayInt?.toOrdinal()
                      : emptyValue}
                  </td>
                )}
                {hasFinancialAidDates && (
                  <td>
                    {applicationDeadlineEarly !== null && applicationDeadlineEarly?.financialAidDate
                      ? applicationDeadlineEarly?.financialAidDate?.monthName + " " + applicationDeadlineEarly?.financialAidDate?.dayInt?.toOrdinal()
                      : emptyValue}
                  </td>
                )}
                {hasRollingAdmissions && (
                  <td>
                    {applicationDeadlineEarly !== null && applicationDeadlineEarly?.isRollingAdmission
                      ? formatMessage({ id: "yes", defaultMessage: "Yes" })
                      : formatMessage({ id: "no", defaultMessage: "No" })}
                  </td>
                )}
              </tr>
              <tr>
                <td>
                  <FormattedMessage id="transfer" defaultMessage="Transfer" />
                </td>
                {hasClosingDates && (
                  <td>
                    {applicationDeadlineTransfer !== null && applicationDeadlineTransfer?.closingDate
                      ? applicationDeadlineTransfer?.closingDate?.monthName + " " + applicationDeadlineTransfer?.closingDate?.dayInt?.toOrdinal()
                      : emptyValue}
                  </td>
                )}
                {hasNotificationDates && (
                  <td>
                    {applicationDeadlineTransfer !== null && applicationDeadlineTransfer?.notificationDate
                      ? applicationDeadlineTransfer?.notificationDate?.monthName + " " + applicationDeadlineTransfer?.notificationDate?.dayInt?.toOrdinal()
                      : emptyValue}
                  </td>
                )}
                {hasFinancialAidDates && (
                  <td>
                    {applicationDeadlineTransfer !== null && applicationDeadlineTransfer?.financialAidDate
                      ? applicationDeadlineTransfer?.financialAidDate?.monthName + " " + applicationDeadlineTransfer?.financialAidDate?.dayInt?.toOrdinal()
                      : emptyValue}
                  </td>
                )}
                {hasRollingAdmissions && (
                  <td>
                    {applicationDeadlineTransfer !== null && applicationDeadlineTransfer?.isRollingAdmission
                      ? formatMessage({ id: "yes", defaultMessage: "Yes" })
                      : formatMessage({ id: "no", defaultMessage: "No" })}
                  </td>
                )}
              </tr>
            </tbody>
          </Table>
          </div>
        </div>
      )}
    </div>
    </>
  )
}

SectionAdmissions.propTypes = {
  schoolData: PropTypes.shape({
    appliedCount: PropTypes.number,
    acceptedCount: PropTypes.number,
    enrolledCount: PropTypes.number,
    averageSatReading: PropTypes.number,
    averageSatReading25th: PropTypes.number,
    averageSatReading75th: PropTypes.number,
    averageSatMath: PropTypes.number,
    averageSatMath25th: PropTypes.number,
    averageSatMath75th: PropTypes.number,
    actCompositeScore: PropTypes.number,
    actCompositeScore25th: PropTypes.number,
    actCompositeScore75th: PropTypes.number,
    applicationFee: PropTypes.shape({
      symbol: PropTypes.string,
      value: PropTypes.number,
    }),
    averageHighSchoolGpa: PropTypes.number,
    requirements: PropTypes.any,
    applicationRequirements: PropTypes.arrayOf(PropTypes.string),
    acceptanceRate: PropTypes.number,
    applicationDeadlineFreshman: PropTypes.any,
    applicationDeadlineEarly: PropTypes.any,
    applicationDeadlineTransfer: PropTypes.any,
  }),
}

SectionAdmissions.defaultProps = {
  schoolData: {},
}

export default SectionAdmissions
